
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

interface FormValues {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    designation?: string;
    joining_date?: string;
    status?: string;
    password: string;
    role_id: string;
    profile_image: FileList;
}

export default function AddEmployee() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canAdd = hasPermission("employee-management.add");
    const [saving, setSaving] = useState(false);
    const [roles, setRoles] = useState<any[]>([]);
    const [isPasswordVisible, setPasswordVisible] = useState(false);
    const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>();

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response: any = await apiPost("role/list", {});
                setRoles(Array.isArray(response) ? response : []);
            } catch (error) {
                console.error(error);
            }
        };
        fetchRoles();
    }, []);

    const onSubmit = async (data: FormValues) => {
        setSaving(true);
        try {
            let profileImageFilename = '';
            if (data.profile_image && data.profile_image.length > 0) {
                profileImageFilename = await uploadFile(data.profile_image[0]);
            }

            const payload: any = { ...data };
            if (profileImageFilename) payload.profile_image = profileImageFilename;
            else delete payload.profile_image;

            await apiPost('employee/store', payload);
            toast.success('Employee added successfully', { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/employee`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Error adding employee');
        } finally {
            setSaving(false);
        }
    };

    if (!canAdd) {
        return (
            <div className="page-body">
                <Breadcrumbs pageTitle="Add Employee" parent="User Management" title="Add Employee" />
                <Container fluid>
                    <Card><CardBody><div className="text-center py-5">No Permission</div></CardBody></Card>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Employee" parent="User Management" title="Add Employee" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Label>Name <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{ required: "Name is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Full Name" invalid={!!errors.name} autoComplete="off" />
                                                <FormFeedback>{errors.name?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Email <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="email"
                                        control={control}
                                        rules={{
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                message: "Invalid email address"
                                            }
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Email Address" invalid={!!errors.email} autoComplete="off" />
                                                <FormFeedback>{errors.email?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Phone <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="phone"
                                        control={control}
                                        rules={{ required: "Phone is required" }}
                                        render={({ field }) => (
                                            <>
                                                <PhoneInput
                                                    country={"in"}
                                                    onlyCountries={["in"]}
                                                    disableDropdown
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    inputProps={{
                                                        name: "phone",
                                                        placeholder: "Enter Phone",
                                                        autoComplete: "none"
                                                    }}
                                                    inputStyle={{
                                                        width: "100%",
                                                        borderColor: errors.phone ? "red" : "#ced4da"
                                                    }}
                                                />
                                                <FormFeedback style={{ display: errors.phone ? "block" : "none" }}>{errors.phone?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Address <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="address"
                                        control={control}
                                        rules={{ required: "Address is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Address" invalid={!!errors.address} autoComplete="off" />
                                                <FormFeedback>{errors.address?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Designation <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="designation"
                                        control={control}
                                        rules={{ required: "Designation is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Designation" invalid={!!errors.designation} autoComplete="off" />
                                                <FormFeedback>{errors.designation?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Joining Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="joining_date"
                                        control={control}
                                        rules={{ required: "Joining Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    wrapperClassName="w-100"
                                                    className={`form-control ${errors.joining_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                    placeholderText="Select Joining Date"
                                                    dateFormat="yyyy-MM-dd"
                                                    maxDate={new Date()}
                                                    peekNextMonth
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                    autoComplete="off"
                                                />
                                                <FormFeedback style={{ display: errors.joining_date ? "block" : "none" }}>{errors.joining_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Status <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{ required: "Status is required" }}
                                        defaultValue="Active"
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.status}>
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </Input>
                                                <FormFeedback>{errors.status?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Role <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="role_id"
                                        control={control}
                                        rules={{ required: "Role is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.role_id}>
                                                    <option value="">Select Role</option>
                                                    {roles.map((r: any) => (
                                                        <option key={r.id} value={r.id}>{r.name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.role_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Profile Image <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="profile_image"
                                        control={control}
                                        rules={{
                                            required: "Profile Image is required",
                                            validate: (files: any) => {
                                                if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                    return "File size must be less than 2MB";
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    onChange={(e) => onChange(e.target.files)}
                                                    accept="image/*"
                                                    invalid={!!errors.profile_image}
                                                />
                                                <FormFeedback>{errors.profile_image?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Password <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="password"
                                        control={control}
                                        rules={{ required: "Password is required", minLength: { value: 6, message: "Min 6 chars" } }}
                                        render={({ field }) => (
                                            <>
                                                <div className="form-input position-relative">
                                                    <Input type={isPasswordVisible ? "text" : "password"} {...field} placeholder="Password" invalid={!!errors.password} autoComplete="off" />
                                                    <div className="show-hide" onClick={togglePasswordVisibility}>
                                                        <span className={!isPasswordVisible ? "show" : ""}></span>
                                                    </div>
                                                </div>
                                                <FormFeedback>{errors.password?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                            </Row>

                            <div className="mt-4">
                                <Btn color="primary" disabled={saving} type="submit">
                                    {saving ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}